import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { Upload, FileText, Image as ImageIcon, Download, Trash2, Loader2, User } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const AdminMealPlans = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [title, setTitle] = useState('');
  const [weekOf, setWeekOf] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const fileRef = useRef(null);

  const loadUsers = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(r.data);
    } catch { /* noop */ }
  };
  const loadPlans = async (uid = '') => {
    try {
      const url = uid ? `${API}/admin/meal-plans?client_user_id=${uid}` : `${API}/admin/meal-plans`;
      const r = await axios.get(url, { withCredentials: true });
      setPlans(r.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (user) { loadUsers(); loadPlans(); }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !selectedUser || !title) {
      toast({ title: 'Missing fields', description: 'Please pick a client, add a title, and choose a file.' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('client_user_id', selectedUser);
      fd.append('title', title);
      fd.append('week_of', weekOf);
      fd.append('notes', notes);
      fd.append('file', file);
      await axios.post(`${API}/admin/meal-plans`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Uploaded!', description: 'Client will see this in their dashboard.' });
      setTitle(''); setWeekOf(''); setNotes(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadPlans(filterUser);
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!confirm('Delete this meal plan?')) return;
    try {
      await axios.delete(`${API}/admin/meal-plans/${id}`, { withCredentials: true });
      loadPlans(filterUser);
      toast({ title: 'Deleted' });
    } catch { toast({ title: 'Delete failed' }); }
  };

  return (
    <div>
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white rounded-3xl p-8 shadow-sm space-y-4">
          <h2 className="font-serif text-2xl text-[#1a2b17] flex items-center gap-2"><Upload className="w-5 h-5" /> Upload meal plan</h2>

          <div>
            <Label>Client</Label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-white">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email} — {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Week 1 - Fat Loss Plan" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label>Week of</Label>
              <Input value={weekOf} onChange={(e) => setWeekOf(e.target.value)} placeholder="July 21, 2025" className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Focus on hydration this week..." className="mt-1.5 rounded-xl min-h-[80px]" />
          </div>

          <div>
            <Label>File (PDF or photo, max 15MB)</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0])}
              className="mt-1.5 block w-full text-sm text-[#1a2b17] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#e8f2e2] file:text-[#3a5a2c] hover:file:bg-[#d5e8ca] cursor-pointer"
            />
            {file && <div className="mt-2 text-xs text-[#7a8471]">Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</div>}
          </div>

          <Button type="submit" disabled={uploading} className="w-full h-12 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">
            {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>) : 'Upload plan'}
          </Button>
        </form>

        <div className="bg-white rounded-3xl p-8 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#1a2b17]">All uploads</h2>
            <Select value={filterUser || 'all'} onValueChange={(v) => { const val = v === 'all' ? '' : v; setFilterUser(val); loadPlans(val); }}>
              <SelectTrigger className="w-52 h-9 rounded-full bg-white text-sm">
                <SelectValue placeholder="Filter by client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {plans.length === 0 && <p className="text-sm text-[#7a8471]">No meal plans uploaded yet.</p>}
          <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {plans.map((p) => (
              <li key={p.id} className="p-4 rounded-2xl bg-[#faf7f1] border border-neutral-100 flex items-center gap-4">
                <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center">
                  {p.kind === 'pdf' ? <FileText className="w-5 h-5 text-[#c9743a]" /> : <ImageIcon className="w-5 h-5 text-[#3a5a2c]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1a2b17] truncate">{p.title}</div>
                  <div className="text-xs text-[#7a8471] truncate flex items-center gap-1"><User className="w-3 h-3" /> {p.client_email} · {p.week_of || '—'}</div>
                </div>
                <a href={`${BASE}${p.file_url}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white hover:bg-[#3a5a2c] hover:text-white flex items-center justify-center transition">
                  <Download className="w-4 h-4" />
                </a>
                <button onClick={() => del(p.id)} className="w-9 h-9 rounded-full bg-white hover:bg-red-500 hover:text-white flex items-center justify-center transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminMealPlans;
